<template>
  <div class="quick-qa">
    <div class="qa-heading"><span>根据已发布指引原文摘录回答</span><el-button type="text" size="mini" :disabled="!messages.length" @click="clearConversation">清空对话</el-button></div>
    <div ref="messages" class="qa-messages" role="log" aria-live="polite" aria-label="操作指引问答记录" :aria-busy="loading ? 'true' : 'false'">
      <div v-if="!messages.length" class="qa-welcome">
        <p>描述您想完成的操作，例如“如何报名培训？”</p>
        <button v-for="(question, index) in suggestions" :key="index" class="suggestion" @click="send(question)">{{ question }}</button>
      </div>
      <article v-for="message in messages" :key="message.id" :class="['qa-message', message.role, { 'qa-error': message.error }]">
        <strong>{{ message.role === 'user' ? '我的问题' : '指引助手' }}</strong>
        <p>{{ message.pending ? '正在查找相关指引并整理回答…' : message.text }}</p>
        <div v-if="message.sources.length" class="qa-sources">
          <small>指引来源</small>
          <button v-for="(source, index) in message.sources" :key="index" class="source-button" @click="$emit('source', source)">{{ source.guideName }} · 步骤 {{ source.stepNo }}</button>
          <details v-for="(source, index) in message.sources" :key="'quote-' + index"><summary>依据原文 {{ index + 1 }}</summary><p>{{ source.quote }}</p></details>
        </div>
        <el-button v-if="message.error" type="text" size="mini" :disabled="loading" @click="retry(message)">{{ message.errorCode === '409' ? '重新提问' : '重试' }}</el-button>
      </article>
    </div>
    <div class="qa-composer">
      <label for="operation-guide-question">您的问题</label>
      <el-input id="operation-guide-question" v-model="question" type="textarea" :rows="2" :maxlength="2000" show-word-limit placeholder="请输入操作问题，Ctrl / Command + Enter 发送" :disabled="loading" @keydown.native="handleKeydown" />
      <div class="qa-send"><small>没有对应指引时会明确提示。</small><el-button type="primary" size="small" :loading="loading" :disabled="!question.trim()" @click="send()">发送</el-button></div>
    </div>
  </div>
</template>

<script>
import { askGuideQuestion, guideQaData, guideQaErrorMessage } from '@/api/operationGuide'

const NO_MATCH = '当前操作指引中暂无相关说明，请联系管理员。'

export default {
  name: 'GuideQuickQa',
  props: { context: { type: Object, required: true }, suggestions: { type: Array, default: () => [] } },
  data() { return { question: '', messages: [], loading: false, conversationId: null, conversationUpdatedAt: 0, requestId: 0, messageId: 0 } },
  beforeDestroy() { this.requestId++; this.conversationId = null },
  methods: {
    clearConversation() { this.requestId++; this.messages = []; this.question = ''; this.loading = false; this.conversationId = null; this.conversationUpdatedAt = 0 },
    scrollToEnd() { this.$nextTick(() => { const node = this.$refs.messages; if (node) node.scrollTop = node.scrollHeight }) },
    handleKeydown(event) { if ((event.ctrlKey || event.metaKey) && event.key === 'Enter' && !event.isComposing) { event.preventDefault(); this.send() } },
    retry(message) {
      if (this.loading) return
      const index = this.messages.indexOf(message)
      if (index === this.messages.length - 1 && index > 0) this.messages.splice(index - 1, 2)
      this.send(message.question)
    },
    async send(value) {
      const question = (typeof value === 'string' ? value : this.question).trim()
      if (!question || question.length > 2000 || this.loading) return
      if (this.conversationId && Date.now() - this.conversationUpdatedAt >= 30 * 60 * 1000) { this.conversationId = null; this.conversationUpdatedAt = 0 }
      const id = ++this.requestId
      this.question = ''; this.loading = true
      const message = { id: ++this.messageId, role: 'assistant', text: '', sources: [], pending: true, error: false, errorCode: '', question }
      this.messages.push({ id: ++this.messageId, role: 'user', text: question, sources: [] }, message)
      if (this.messages.length > 40) this.messages.splice(0, this.messages.length - 40)
      this.scrollToEnd()
      try {
        const data = guideQaData(await askGuideQuestion({ ...this.context, question, conversationId: this.conversationId }))
        if (id !== this.requestId || this._isDestroyed) return
        if (!['ANSWER', 'NO_MATCH', 'CLARIFY'].includes(data.answerType)) throw new Error()
        if (data.answerType === 'ANSWER') {
          // 无引用的模型回复不能作为有据答案展示；引用正文由后端做权限和原文校验。
          if (typeof data.answer !== 'string' || !data.answer.trim() || data.answer.length > 20000 || !Array.isArray(data.sources) || !data.sources.length || data.sources.length > 8 || data.sources.some(source => !source || !['string', 'number'].includes(typeof source.guideId) || !String(source.guideId).trim() || typeof source.guideName !== 'string' || !source.guideName.trim() || !Number.isInteger(source.stepNo) || source.stepNo < 1 || typeof source.quote !== 'string' || !source.quote.trim() || source.quote.length > 10000) || data.sources.reduce((total, source) => total + source.quote.length, 0) > 12000) throw new Error()
          message.text = data.answer
          message.sources = data.sources.map(source => ({ guideId: source.guideId, guideName: source.guideName, stepNo: source.stepNo, quote: source.quote, version: source.version }))
        } else if (data.answerType === 'NO_MATCH') {
          message.text = NO_MATCH
        } else {
          // 澄清仅展示固定提问，避免无引用的自由文本被用来绕过答案约束。
          message.text = '请补充您所在的模块、页面和具体想完成的操作，便于查找对应指引。'
        }
        this.conversationId = typeof data.conversationId === 'string' && data.conversationId.trim() && data.conversationId.length <= 200 ? data.conversationId : null
        this.conversationUpdatedAt = this.conversationId ? Date.now() : 0
      } catch (e) {
        if (id !== this.requestId || this._isDestroyed) return
        message.error = true
        message.errorCode = String(e && e.code || '')
        message.text = guideQaErrorMessage(e)
        message.sources = []
        if (['409', '401', '403'].includes(message.errorCode)) {
          this.conversationId = null
          this.conversationUpdatedAt = 0
          this.$emit('refresh-capabilities')
        }
      } finally {
        if (id === this.requestId && !this._isDestroyed) { message.pending = false; this.loading = false; this.scrollToEnd() }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.quick-qa { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.qa-heading, .qa-send { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.qa-heading, small { color: #606266; font-size: 12px; line-height: 1.6; }
.qa-messages { flex: 1; min-height: 0; overflow-y: auto; padding: 8px 0; overscroll-behavior: contain; }
.qa-welcome { color: #606266; line-height: 1.7; font-size: 13px; }
.qa-message { padding: 12px; margin-bottom: 12px; background: #f5f7fa; border-radius: 6px; font-size: 13px; }
.qa-message.user { background: #edf5ff; }
.qa-message.qa-error { background: #fff7ed; }
.qa-message strong { font-size: 12px; color: #606266; }
.qa-message p { margin: 8px 0 0; white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.8; }
.qa-sources { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; }
.source-button, .suggestion { border: 1px solid #d8e6fa; padding: 8px; background: #fff; border-radius: 4px; color: #175db0; text-align: left; cursor: pointer; overflow-wrap: anywhere; }
.suggestion { display: block; margin-top: 8px; width: 100%; }
summary { color: #606266; cursor: pointer; font-size: 12px; }
.qa-composer { padding-top: 12px; border-top: 1px solid #ebeef5; flex: none; }
.qa-composer label { display: block; font-size: 12px; color: #606266; margin-bottom: 6px; }
.qa-send { margin-top: 8px; }
button:focus-visible, summary:focus-visible { outline: 2px solid #175db0; outline-offset: 2px; }
</style>
